/**
 * Telegram 登录管理 - 必要核心文件智能白名单过滤器
 * 仅上传与解析 Telegram 登录所必需的 session/json/tdata 核心密钥文件
 * 自动拦截并剔除：多媒体图片/视频、可执行程序(.exe/.dll)、系统残留(.DS_Store/desktop.ini)、
 * 崩溃转储(.dmp/.dump)、运行日志(.log)、以及 tdata 内部的 cache/dumps/media 等无用大文件。
 */

export interface FilterResult {
  isLoginEssential: boolean;
  reason?: string;
}

// 严禁上传的无用后缀名
const BANNED_EXTENSIONS = new Set([
  // 图像与多媒体
  ".png", ".jpg", ".jpeg", ".webp", ".gif", ".ico", ".bmp", ".svg", ".tiff", ".psd",
  ".mp4", ".mp3", ".ogg", ".wav", ".webm", ".avi", ".mov", ".m4a", ".flac", ".mkv",
  // 可执行文件与动态库
  ".exe", ".dll", ".so", ".dylib", ".bin", ".msi", ".apk", ".iso", ".sys", ".drv",
  // 压缩包与镜像 (单文件上传时不需要重复嵌套)
  ".rar", ".7z", ".tar", ".gz", ".bz2", ".xz",
  // 日志与转储
  ".log", ".tmp", ".temp", ".dump", ".dmp", ".crash", ".bak", ".old", ".swp",
  // 网页与文档杂项
  ".html", ".htm", ".css", ".pdf", ".docx", ".xlsx", ".pptx", ".md"
]);

// 严禁上传的垃圾目录关键字 (路径中包含即过滤)
const BANNED_PATH_SEGMENTS = [
  "/cache/",
  "\\cache\\",
  "/dumps/",
  "\\dumps\\",
  "/crashpad/",
  "\\crashpad\\",
  "/blob_storage/",
  "\\blob_storage\\",
  "/gpucache/",
  "\\gpucache\\",
  "/media/",
  "\\media\\",
  "/downloads/",
  "\\downloads\\",
  "/logs/",
  "\\logs\\",
  "/temp/",
  "\\temp\\",
  "/temp_data/",
  "\\temp_data\\",
  "__macosx",
  "/.git/",
  "\\.git\\",
  "/.vscode/",
  "\\.vscode\\",
  "/node_modules/",
  "\\node_modules\\"
];

// 系统无用隐藏文件名
const BANNED_EXACT_FILENAMES = new Set([
  "desktop.ini",
  "thumbs.db",
  ".ds_store",
  ".gitignore",
  "telegram.exe",
  "updater.exe",
  "log.txt",
  "debug.log",
  "crashpad_handler.exe"
]);

export type TgLoginImportMode = "all" | "protocol" | "tdata";

/**
 * 判断是否为电话号码格式的文件名
 * 例如：13313214208, +13313214208, 8613313214208, 13313214208_密钥
 */
export function isPhoneNumberFormatName(fileNameOrBase: string): boolean {
  if (!fileNameOrBase) return false;
  // 去除扩展名
  const baseName = fileNameOrBase.replace(/\.(json|session|txt|dat)$/i, "").trim();
  const digits = baseName.replace(/\D/g, "");
  
  // 电话号码纯数字长度通常在 6 到 16 位之间
  if (digits.length < 6 || digits.length > 16) {
    return false;
  }

  // 过滤掉包含明确非电话号码关键词的文件 (如 shortcuts, custom, default, package, tsconfig 等)
  const lower = baseName.toLowerCase();
  if (
    lower.includes("shortcut") ||
    lower.includes("custom") ||
    lower.includes("default") ||
    lower.includes("package") ||
    lower.includes("tsconfig") ||
    lower.includes("metadata") ||
    lower.includes("setting") ||
    lower.includes("config")
  ) {
    return false;
  }

  // 允许纯数字、带前缀+、带下划线后缀(如 13313214208_密钥 或 13313214208_2fa)
  if (/^[+]?[0-9\s\-_()]+$/.test(baseName) || baseName.startsWith(digits)) {
    return true;
  }

  return false;
}

/**
 * 判断单个文件是否属于 Telegram 登录所必需的核心文件
 * @param fileName 文件名 (如 13313214208.session, 13313214208.json, 2fa.txt)
 * @param relativePath 相对路径
 * @param fileSize 文件大小 (字节)
 * @param mode 导入模式："protocol" (协议号: 仅抓取电话号码格式的.json/.session及配套txt), "tdata" (直登号: 仅tdata核心), "all" (二者兼备)
 */
export function isTelegramLoginEssentialFile(
  fileName: string,
  relativePath: string = "",
  fileSize?: number,
  mode: TgLoginImportMode = "all"
): FilterResult {
  const nameClean = (fileName || "").trim();
  const nameLower = nameClean.toLowerCase();
  const fullPathLower = (relativePath || nameClean).toLowerCase().replace(/\\/g, "/");

  // 1. 拦截大文件 (单个 session/tdata 密钥文件通常不超过 25MB)
  if (fileSize && fileSize > 25 * 1024 * 1024) {
    return { isLoginEssential: false, reason: "文件大小超过 25MB，非有效登录凭证文件" };
  }

  // 2. 拦截系统垃圾与隐藏文件
  if (nameLower.startsWith(".") && !nameLower.endsWith(".session") && !nameLower.endsWith(".json")) {
    return { isLoginEssential: false, reason: "系统隐藏文件" };
  }
  if (BANNED_EXACT_FILENAMES.has(nameLower)) {
    return { isLoginEssential: false, reason: "系统或可执行程序垃圾文件" };
  }

  // 3. 拦截路径中包含垃圾缓存/转储目录的文件
  for (const seg of BANNED_PATH_SEGMENTS) {
    if (fullPathLower.includes(seg.replace(/\\/g, "/"))) {
      return { isLoginEssential: false, reason: `属于临时缓存或转储目录 (${seg})` };
    }
  }

  // 4. 拦截明确禁止的扩展名 (媒体/exe/dll/log/zip等)
  const dotIdx = nameLower.lastIndexOf(".");
  if (dotIdx !== -1) {
    const ext = nameLower.substring(dotIdx);
    if (BANNED_EXTENSIONS.has(ext)) {
      return { isLoginEssential: false, reason: `不受支持的媒体/程序/日志文件 (${ext})` };
    }
  }

  const isProtocolExt = nameLower.endsWith(".session") || nameLower.endsWith(".json");
  const is2FaTxt = (nameLower.endsWith(".txt") && (nameLower.includes("2fa") || nameLower.includes("密钥") || nameLower.includes("password") || isPhoneNumberFormatName(nameClean)));

  // 5. 协议号模式 (Protocol Mode): 仅抓取电话号码格式的 .session 与 .json 核心凭据文件及 2fa 密钥
  if (mode === "protocol") {
    if (isProtocolExt) {
      if (isPhoneNumberFormatName(nameClean)) {
        return { isLoginEssential: true };
      }
      return { 
        isLoginEssential: false, 
        reason: `非电话号码格式的协议文件 (${fileName})，协议号模式已自动忽略` 
      };
    }
    if (is2FaTxt) {
      return { isLoginEssential: true };
    }
    return { 
      isLoginEssential: false, 
      reason: "协议号模式下仅抓取电话号码格式的 .json 与 .session 核心凭据文件" 
    };
  }

  // 6. 直登号模式与通用混合模式下的 TData 核心凭据判断
  // 检查是否位于 tdata 目录中或包含 tdata 路径特征
  const isTdataFolder = 
    fullPathLower.includes("/tdata/") || 
    fullPathLower.startsWith("tdata/") || 
    fullPathLower.includes("\\tdata\\") || 
    fullPathLower.startsWith("tdata\\") ||
    fullPathLower.includes("key_data");

  // 检查是否包含 tdata 内部明确的非登录垃圾目录 (如 webview, user_data, emoji, stickers, cache, dumps 等)
  const isTdataJunkDir =
    fullPathLower.includes("/user_data/") ||
    fullPathLower.includes("\\user_data\\") ||
    fullPathLower.includes("/webview/") ||
    fullPathLower.includes("\\webview\\") ||
    fullPathLower.includes("/emoji/") ||
    fullPathLower.includes("\\emoji\\") ||
    fullPathLower.includes("/stickers/") ||
    fullPathLower.includes("\\stickers\\") ||
    fullPathLower.includes("/icons/") ||
    fullPathLower.includes("\\icons\\") ||
    fullPathLower.includes("/working/") ||
    fullPathLower.includes("\\working\\");

  if (isTdataJunkDir) {
    return { isLoginEssential: false, reason: "属于 tdata 内部非必要媒体或界面缓存文件" };
  }

  // 明确的 TData 核心密钥与配置数据文件特征
  const isTdataCoreFile =
    nameClean === "key_datas" ||
    nameClean === "key_data" ||
    nameClean.startsWith("key_data") ||
    nameClean.startsWith("key_datas") ||
    nameClean.startsWith("D877F783D5D3EF8C") ||
    nameClean.startsWith("maps") ||
    nameClean.startsWith("map") ||
    nameClean.startsWith("settings") ||
    nameClean.startsWith("shortcuts") ||
    nameClean.startsWith("configs") ||
    nameClean.startsWith("config") ||
    nameClean.startsWith("usertag") ||
    nameClean.startsWith("countries") ||
    nameClean.startsWith("prefix") ||
    /^[0-9A-Fa-f]{16}[0-9A-Za-z_]*$/.test(nameClean);

  // 16进制账号数据子目录内部的有效核心数据文件 (例如 tdata/D877F783D5D3EF8C/0 或 1 或 data)
  const pathParts = fullPathLower.split("/").filter(Boolean);
  const parentSegment = pathParts.length >= 2 ? pathParts[pathParts.length - 2] : "";
  const isInside16HexAccountDir = /^[0-9a-f]{16}/i.test(parentSegment);
  const isAccountSubdataFile = isInside16HexAccountDir && (
    nameClean === "0" ||
    nameClean === "1" ||
    nameClean === "data" ||
    nameClean === "version" ||
    nameClean.startsWith("map") ||
    nameClean.startsWith("config") ||
    nameClean.startsWith("setting") ||
    /^[0-9A-Fa-f]{16}[0-9A-Za-z_]*$/.test(nameClean)
  );

  const isCompanionTxtOrJson =
    nameLower.endsWith(".txt") ||
    nameLower.endsWith(".json") ||
    nameLower.endsWith(".session") ||
    nameLower.includes("2fa") ||
    nameLower.includes("account") ||
    nameLower.includes("info") ||
    nameLower.includes("password") ||
    isPhoneNumberFormatName(nameClean);

  if (mode === "tdata") {
    if (isTdataCoreFile || isAccountSubdataFile || isCompanionTxtOrJson) {
      return { isLoginEssential: true };
    }

    return { 
      isLoginEssential: false, 
      reason: "属于直登号模式但非 tdata 核心密钥或配套账号有效文件" 
    };
  }

  // 7. 全自动智能识别模式 (mode === "all" / 默认智能模式)
  // 核心逻辑：优先识别 .session 和 .json 协议文件；若无或需兜底自愈则识别 tdata 核心有效文件
  if (isProtocolExt) {
    const isIgnoredJson = 
      nameLower === "package.json" || 
      nameLower === "tsconfig.json" || 
      nameLower === "shortcuts.json" || 
      nameLower === "components.json" ||
      nameLower === "metadata.json" ||
      nameLower === "package-lock.json";
    if (!isIgnoredJson) {
      return { isLoginEssential: true };
    }
  }

  if (is2FaTxt || isCompanionTxtOrJson) {
    return { isLoginEssential: true };
  }

  // 仅采集并上传 tdata 核心密钥、配置数据与 16 进制子目录数据文件
  if (isTdataCoreFile || isAccountSubdataFile) {
    return { isLoginEssential: true };
  }

  return { isLoginEssential: false, reason: "非 Telegram 必需登录凭据核心文件 (已智能过滤无关文件)" };
}

/**
 * 批量过滤文件列表，仅提取登录核心文件并统计过滤数据
 */
export function filterTelegramLoginFiles<T extends { name?: string; webkitRelativePath?: string; size?: number; path?: string }>(
  files: T[],
  mode: TgLoginImportMode = "all"
): {
  essentialFiles: T[];
  skippedCount: number;
  skippedReasons: Record<string, number>;
} {
  const essentialFiles: T[] = [];
  let skippedCount = 0;
  const skippedReasons: Record<string, number> = {};

  for (const f of files) {
    const fileName = f.name || (f.path ? f.path.split("/").pop() || f.path : "");
    const relativePath = f.webkitRelativePath || f.path || fileName;
    const size = f.size;

    const result = isTelegramLoginEssentialFile(fileName, relativePath, size, mode);
    if (result.isLoginEssential) {
      essentialFiles.push(f);
    } else {
      skippedCount++;
      const reason = result.reason || "无关文件";
      skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
    }
  }

  return {
    essentialFiles,
    skippedCount,
    skippedReasons
  };
}

/**
 * 账号凭证文件智能裁决器：严格遵循【.session 和 .json 优先】原则
 * 当同一目录或同一账号下同时存在协议文件 (.session / .json) 与 tdata 文件夹时：
 * 自动优先保留 .session 与 .json 协议文件，剔除冗余的 tdata 内部所有文件。
 * 这将传输文件数量由十几个骤降为 1~2 个，体积减少 95% 以上，彻底杜绝单批次过大或超时 504 错误。
 * 只有当某个账号完全不存在 .session 或 .json 时（纯直登号），才保留其 tdata 核心文件。
 */
export function prioritizeSessionFilesOverTdata<T extends { name?: string; webkitRelativePath?: string; size?: number; path?: string }>(
  files: T[]
): {
  optimizedFiles: T[];
  prunedTdataCount: number;
  protocolAccountsCount: number;
  tdataAccountsCount: number;
} {
  if (!Array.isArray(files) || files.length === 0) {
    return { optimizedFiles: [], prunedTdataCount: 0, protocolAccountsCount: 0, tdataAccountsCount: 0 };
  }

  // 1. 按所属账号/文件夹对文件进行聚类分析
  const accountBuckets: Record<string, T[]> = {};

  for (const f of files) {
    const rawPath = (f.path || f.webkitRelativePath || f.name || "").replace(/\\/g, "/");
    const parts = rawPath.split("/").filter(Boolean);
    let bucketKey = "root_account";

    if (parts.length > 1) {
      // 多级路径：若第二级为 tdata，则顶级目录即为账号目录 (例如 Account_1/tdata/key_datas -> Account_1)
      bucketKey = parts[0];
    } else if (parts.length === 1) {
      const baseName = parts[0];
      const isTdataComponent =
        baseName.startsWith("key_data") ||
        baseName.startsWith("D877") ||
        baseName.startsWith("map") ||
        baseName.startsWith("setting") ||
        baseName === "prefix" ||
        baseName === "usertag" ||
        baseName === "0" ||
        baseName === "1" ||
        baseName.startsWith("shortcuts") ||
        /^[0-9A-Fa-f]{16}/.test(baseName);

      if (isTdataComponent) {
        bucketKey = "root_tdata_account";
      } else {
        const dotIdx = baseName.lastIndexOf(".");
        const stem = dotIdx > 0 ? baseName.substring(0, dotIdx) : baseName;
        bucketKey = `account_${stem}`;
      }
    }

    if (!accountBuckets[bucketKey]) {
      accountBuckets[bucketKey] = [];
    }
    accountBuckets[bucketKey].push(f);
  }

  const optimizedFiles: T[] = [];
  let prunedTdataCount = 0;
  let protocolAccountsCount = 0;
  let tdataAccountsCount = 0;

  // 2. 检查全局是否包含协议文件（如果用户直接拖放了混杂在一起的文件）
  const allFilesHasProtocol = files.some(f => {
    const p = (f.path || f.webkitRelativePath || f.name || "").toLowerCase();
    return p.endsWith(".session") || p.endsWith(".json");
  });

  for (const bucketKey of Object.keys(accountBuckets)) {
    const bucketFiles = accountBuckets[bucketKey];
    
    // 判断当前账号桶内是否具备 .session 或 .json 协议凭据
    const hasProtocolInBucket = bucketFiles.some(f => {
      const p = (f.path || f.webkitRelativePath || f.name || "").toLowerCase();
      return p.endsWith(".session") || p.endsWith(".json");
    });

    // 若本桶有协议文件，或者在单层混合根目录下且全局存在协议文件
    const shouldPrioritizeProtocol = hasProtocolInBucket || (bucketKey === "root_tdata_account" && allFilesHasProtocol);

    if (shouldPrioritizeProtocol) {
      protocolAccountsCount++;
      for (const item of bucketFiles) {
        const p = (item.path || item.webkitRelativePath || item.name || "").replace(/\\/g, "/");
        const pLower = p.toLowerCase();
        const baseName = p.split("/").pop() || p;
        const isTdataSubfile =
          pLower.includes("/tdata/") ||
          pLower.startsWith("tdata/") ||
          baseName.startsWith("key_data") ||
          baseName.startsWith("D877") ||
          baseName.startsWith("maps") ||
          baseName.startsWith("settings") ||
          baseName === "prefix" ||
          baseName === "usertag" ||
          baseName === "0" ||
          baseName === "1" ||
          /^[0-9A-Fa-f]{16}/.test(baseName);

        if (isTdataSubfile) {
          // 该账号已具备 .session/.json 协议文件，自动跳过其冗余 tdata 内部文件
          prunedTdataCount++;
        } else {
          optimizedFiles.push(item);
        }
      }
    } else {
      // 该账号无任何 .session/.json 协议凭据，保留 tdata 直登核心文件
      tdataAccountsCount++;
      optimizedFiles.push(...bucketFiles);
    }
  }

  return {
    optimizedFiles,
    prunedTdataCount,
    protocolAccountsCount,
    tdataAccountsCount
  };
}

/**
 * 智能将大量 Telegram 账号文件切分为安全的小批次（Chunking）
 * 彻底解决单次 HTTP 请求载荷过大导致的 413 Request Entity Too Large 错误
 * 规则：
 * 1. 严格优先选用 .session 与 .json 协议文件
 * 2. 同一目录/同一账号关联文件归入同一个组，确保不被割裂
 * 3. 每个切片最大包含 maxFilesPerChunk 个文件（默认 20 个），且 Base64 总体积不超过 maxBytesPerChunk（默认 2.5MB）
 */
export function chunkTelegramFiles<T extends { name?: string; path?: string; content?: string }>(
  files: T[],
  maxFilesPerChunk = 20,
  maxBytesPerChunk = 2.5 * 1024 * 1024
): T[][] {
  if (!Array.isArray(files) || files.length === 0) return [];

  // 先对整体进行协议优先优化裁决
  const { optimizedFiles } = prioritizeSessionFilesOverTdata(files);
  const targetFiles = optimizedFiles.length > 0 ? optimizedFiles : files;

  // 按文件夹或文件名前缀进行账号同组归类
  const groups: Record<string, T[]> = {};

  for (const f of targetFiles) {
    const rawPath = (f.path || f.name || "").replace(/\\/g, "/");
    const parts = rawPath.split("/").filter(Boolean);
    let groupKey = "root_ungrouped";

    if (parts.length > 1) {
      // 拥有多级目录，例如: Account_01/tdata/... 或 Account_01/+123456.session
      groupKey = parts[0];
    } else if (parts.length === 1) {
      // 单层目录或直接文件
      const baseName = parts[0];
      const isTdataName = 
        baseName.startsWith("key_data") || 
        baseName.startsWith("D877") || 
        baseName.startsWith("map") || 
        baseName.startsWith("setting") || 
        baseName === "prefix" || 
        baseName === "usertag" || 
        baseName === "0" ||
        baseName === "1" ||
        baseName.startsWith("shortcuts") ||
        /^[0-9A-Fa-f]{16}/.test(baseName);

      if (isTdataName) {
        groupKey = "root_tdata_single_account";
      } else {
        const dotIndex = baseName.lastIndexOf(".");
        const mainName = dotIndex > 0 ? baseName.substring(0, dotIndex) : baseName;
        groupKey = `single_${mainName}`;
      }
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(f);
  }

  const chunks: T[][] = [];
  let currentChunk: T[] = [];
  let currentBytes = 0;

  // 优先级排序：包含 .session 或 .json 协议文件的组排在最前面优先处理
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    const aHasProtocol = groups[a].some(f => {
      const n = (f.name || f.path || "").toLowerCase();
      return n.endsWith(".session") || n.endsWith(".json");
    });
    const bHasProtocol = groups[b].some(f => {
      const n = (f.name || f.path || "").toLowerCase();
      return n.endsWith(".session") || n.endsWith(".json");
    });
    if (aHasProtocol && !bHasProtocol) return -1;
    if (!aHasProtocol && bHasProtocol) return 1;
    return a.localeCompare(b);
  });

  for (const key of sortedKeys) {
    const groupFiles = groups[key];
    let groupBytes = 0;
    for (const item of groupFiles) {
      groupBytes += (item.content ? item.content.length : 0);
    }

    // 若当前 Chunk 已有文件，且加入该组后超出限制，则将当前 Chunk 封口并开启新 Chunk
    if (
      currentChunk.length > 0 &&
      (currentChunk.length + groupFiles.length > maxFilesPerChunk || currentBytes + groupBytes > maxBytesPerChunk)
    ) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentBytes = 0;
    }

    // 将整个组加入当前 chunk
    currentChunk.push(...groupFiles);
    currentBytes += groupBytes;

    // 若当前 chunk 达到上限，直接封口
    if (currentChunk.length >= maxFilesPerChunk || currentBytes >= maxBytesPerChunk) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentBytes = 0;
    }
  }

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
}
